import { memo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { isKillablePlaybookType } from "@/app-utils/playbook.ts";
import type { Capability, User } from "@/graphql.js";
import { checkCapability } from "@/modules/users/utils/user.js";
import { type IEventState } from "@/redux/modules/event.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { CAPABILITY, EVENT_STATUS } from "@/settings/settings.js";
import KillWorkflowButton from "@/ui-modules/core/KillWorkflowButton.js";

function KillWorkflowButtonHeader() {
  const session = useSelector<IAppState, User>((state) => state.auth.session, shallowEqual);
  const { event } = useSelector<IAppState, IEventState>((state) => state.event, shallowEqual);

  const workflowEventId = event?.id;

  const isOwner = event?.owner.id === session.id;
  const isAdmin = checkCapability(session.capabilities as Capability[], CAPABILITY.ADMINISTRATION);

  const isKillablePlaybook = isKillablePlaybookType(event?.playbook);

  // Hide the button if the playbook type is not killable
  if (!workflowEventId || !isKillablePlaybook) {
    return;
  }

  const isTerminated = !(
    event?.status === EVENT_STATUS.QUEUED ||
    event?.status === EVENT_STATUS.INITIALIZING ||
    event?.status === EVENT_STATUS.RUNNING
  );
  return (
    <KillWorkflowButton
      selectedIds={[workflowEventId]}
      tooltip={
        isOwner || isAdmin
          ? isTerminated
            ? "The job is already terminated."
            : "Terminate the running job."
          : "You must be the owner of the session."
      }
      isDisabled={isTerminated || !(isOwner || isAdmin)}
    />
  );
}

export default memo(KillWorkflowButtonHeader);
